import PageWrapper from '../../components/PageWrapper'
import profileIcon from '../../assets/icons/profile-icon.svg'
import lock from '../../assets/icons/locked.png'
import DefaultButton from '../../components/Button/Button'
import arrow from '../../assets/icons/arrowRight.png'
import padlock from '../../assets/icons/padlock.png'
import toggleOff from '../../assets/icons/toggleOff.png'
import blackProfile from '../../assets/icons/blackProfile.png'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import toggleOn from '../../assets/icons/toggleOn.png'
import React from 'react'
import { showToast } from '../../utils/toast'
import StaffService from '../../api/staff-service'
import { StaffProfileDTO, TwoFactorAuth } from '../../interfaces/staff'
import { QrCodeModal } from '../../components/Modal/Profile/QrCodeModal'
import { handleVerifyEmail } from '../../utils/EmailService'
import Metadata from '../../components/Metadata'

  const settingsMetadata = {
    title: 'Settings - Safelto',
    description: 'Manage your account settings, change password, enable 2FA, and edit profile information. Complete account management solution.',
    keywords: ['settings', 'account settings', 'change password', '2FA', 'profile management'],
    image: '/assets/images/safelto-big.png',
    url: 'https://safelto.com/settings',
    type: 'website' as 'website'
  }

  const Settings = () => {
    const [isToggled, setIsToggled] = useState(false)
    const navigate = useNavigate()
    const [loading, setLoading] = React.useState(true)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [userData, setUserData] = React.useState<StaffProfileDTO | null>(null)
    const [qrCodeData, setQrCodeData] = useState<TwoFactorAuth['staff2Fa'] | null>(null)

    const handleCloseModal = () => {
      setIsModalOpen(false)
    }

    React.useEffect(() => {
      StaffService.getProfile()
        .then((data) => {
          if (data) {
            setUserData(data)
            setIsToggled(data.is2FaEnabled)
          }
          setLoading(false)
        })
        .catch((err) => {
          showToast({
            description: err.message,
            status: 'error',
            id: 'get-staff-error',
            title: 'Error fetching staff details',
          })
        })
        .finally(() => {
          setLoading(false)
        })
    }, [])

    const handleButtonClick = () => {
      const enable2fa = !isToggled
      setLoading(true)

      StaffService.update2FA(enable2fa)
        .then((data) => {
          const qrCodeUrl = data.staff2Fa.qrCodeUrl
          if (!qrCodeUrl) {
            console.warn('QR Code URL is missing or undefined')
          }

          setQrCodeData({ ...data.staff2Fa })

          if (enable2fa === true) {
            setIsModalOpen(true)
          } else {
            setIsModalOpen(false)
            setIsToggled(!isToggled)
          }
        })
        .catch((err) => {
          showToast({
            title: 'Error updating 2FA',
            description: err.message,
            id: 'update-2fa-error',
            status: 'error',
          })
        })
        .finally(() => {
          setLoading(false)
        })
    }

    const verifyEmail = () => {
      handleVerifyEmail(setLoading)
    }

    return (
      <PageWrapper title="Settings" description="Password and Account settings">
        <Metadata {...settingsMetadata} />
      <div className="flex flex-col gap-6 sm:gap-8 md:gap-10 px-3 sm:px-4 md:px-0">
        {/* Page Title */}
        <h1 className="text-2xl sm:text-3xl md:text-[28px] font-medium">
          My Profile
        </h1>

        {/* Profile Section - Mobile Responsive */}
        {loading ? (
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 md:gap-10 mb-6 sm:mb-8 md:mb-14 animate-pulse bg-[#F8F9FA] p-4 sm:p-6 md:p-8 rounded-lg">
            {/* Profile Image Skeleton */}
            <div className="bg-[#E9ECEF] h-[140px] w-[140px] sm:h-[160px] sm:w-[160px] md:h-[170px] md:w-[160px] flex items-center rounded-lg justify-center">
              <div className="h-[80px] w-[80px] sm:h-[90px] sm:w-[90px] md:h-[100px] md:w-[100px] bg-gray-300 rounded-full"></div>
            </div>

            {/* User Info Skeleton */}
            <div className="flex flex-col gap-3 sm:gap-4 w-full sm:w-auto">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
                <div className="h-4 bg-gray-300 rounded w-20 sm:w-24"></div>
                <div className="h-4 bg-gray-300 rounded w-40 sm:w-48 md:w-56"></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
                <div className="h-4 bg-gray-300 rounded w-20 sm:w-24"></div>
                <div className="h-4 bg-gray-300 rounded w-48 sm:w-56 md:w-64"></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
                <div className="h-4 bg-gray-300 rounded w-20 sm:w-24"></div>
                <div className="h-4 bg-gray-300 rounded w-32 sm:w-36 md:w-40"></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
                <div className="h-4 bg-gray-300 rounded w-20 sm:w-24"></div>
                <div className="h-4 bg-gray-300 rounded w-48 sm:w-56 md:w-64"></div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`flex flex-col sm:flex-row items-center gap-6 sm:gap-8 md:gap-10 ${
              !userData?.isEmailVerified ? '' : 'mb-6 sm:mb-8 md:mb-14'
            } bg-[#F8F9FA] p-4 sm:p-6 md:p-8 rounded-lg`}
          >
            {/* Profile Image */}
            <div className="bg-[#E9ECEF] h-[140px] w-[140px] sm:h-[160px] sm:w-[160px] md:h-[170px] md:w-[160px] flex items-center rounded-lg justify-center overflow-hidden">
              <img
                src={userData?.imageUrl ?? profileIcon}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </div>

            {/* User Information */}
            <div className="flex flex-col gap-3 sm:gap-4 w-full">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
                <h3 className="font-normal text-sm sm:text-base text-[#6C757D] min-w-[100px]">
                  Fullname
                </h3>
                <h3 className="font-normal text-sm sm:text-base text-black break-words flex-1">
                  {userData?.name}
                </h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
                <h3 className="font-normal text-sm sm:text-base text-[#6C757D] min-w-[100px]">
                  Email Address
                </h3>
                <h3 className="font-normal text-sm sm:text-base text-black break-words flex-1">
                  {userData?.email}
                </h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
                <h3 className="font-normal text-sm sm:text-base text-[#6C757D] min-w-[100px]">
                  Phone number
                </h3>
                <h3 className="font-normal text-sm sm:text-base text-black">
                  {userData?.phone}
                </h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
                <h3 className="font-normal text-sm sm:text-base text-[#6C757D] min-w-[100px]">
                  Address
                </h3>
                <h3 className="font-normal text-sm sm:text-base text-black break-words flex-1">
                  {userData?.address}
                </h3>
              </div>
            </div>
          </div>
        )}

        {/* Email Verification - Mobile Responsive */}
        {!userData?.isEmailVerified && (
          <div className="cursor-pointer mb-4 sm:mb-6 md:mb-10" onClick={verifyEmail}>
            {loading ? (
              <div className="h-5 w-5 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
            ) : (
              <p className="text-blue-600 font-bold text-sm sm:text-base underline text-center sm:text-left">
                Verify Email
              </p>
            )}
          </div>
        )}

        {/* Action Buttons - Mobile Responsive Grid */}
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 items-stretch justify-center mt-2 sm:mt-4">
          {/* Change Password Button */}
          <DefaultButton
            buttonType="primary"
            width="100%"
            paddingTop="40px"
            paddingBottom="40px"
            paddingLeft="8px"
            paddingRight="40px"
            className="w-full lg:w-[319px] flex items-center justify-between bg-gray-100 rounded-lg py-3 sm:py-4 px-3 sm:px-4 shadow-md hover:bg-gray-200 transition-colors"
            onClick={() => {
              navigate('/settings/change-password')
            }}
          >
            <div className="flex items-center gap-3 sm:gap-4 flex-1">
              <img
                src={lock}
                alt="Lock Icon"
                className="w-7 h-7 sm:w-8 sm:h-8 bg-black rounded-lg p-1.5 sm:p-2"
              />
              <p className="text-xs sm:text-[13px] font-normal flex-1 text-left">
                Change password
              </p>
              <div className="flex-shrink-0">
                <img src={arrow} alt="Arrow Icon" className="w-4 h-4 sm:w-auto" />
              </div>
            </div>
          </DefaultButton>

          {/* Enable 2FA Button */}
          <DefaultButton
            buttonType="primary"
            width="100%"
            paddingTop="40px"
            paddingBottom="40px"
            paddingLeft="2px"
            paddingRight="40px"
            className="w-full lg:w-[319px] flex items-center justify-between bg-gray-100 rounded-lg py-3 sm:py-4 px-3 sm:px-4 shadow-md hover:bg-gray-200 transition-colors"
          >
            <div className="flex items-center gap-3 sm:gap-4 flex-1">
              <img
                src={padlock}
                alt="Padlock Icon"
                className="w-7 h-7 sm:w-8 sm:h-8 bg-black rounded-lg p-1.5 sm:p-2"
              />
              <p className="text-xs sm:text-[13px] font-normal flex-1 text-left">
                Enable 2FA
              </p>
              <div
                onClick={handleButtonClick}
                className="w-[35px] sm:w-[41px] h-[20px] sm:h-[24px] flex items-center justify-center cursor-pointer flex-shrink-0"
              >
                {loading ? (
                  <div className="h-4 w-4 sm:h-5 sm:w-5 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
                ) : (
                  <img 
                    src={isToggled ? toggleOn : toggleOff} 
                    alt="Toggle" 
                    className="w-8 sm:w-auto"
                  />
                )}
              </div>
            </div>
          </DefaultButton>

          {/* Edit Profile Button */}
          <DefaultButton
            buttonType="primary"
            width="100%"
            paddingTop="40px"
            paddingBottom="40px"
            paddingLeft="2px"
            paddingRight="40px"
            className="w-full lg:w-[319px] flex items-center justify-between bg-gray-100 rounded-lg py-3 sm:py-4 px-3 sm:px-4 shadow-md hover:bg-gray-200 transition-colors"
            onClick={() => {
              navigate('/settings/edit-profile')
            }}
          >
            <div className="flex items-center gap-3 sm:gap-4 flex-1">
              <img
                src={blackProfile}
                alt="Profile Icon"
                className="w-7 h-7 sm:w-8 sm:h-8 bg-black rounded-lg p-1.5 sm:p-2"
              />
              <p className="text-xs sm:text-[13px] font-normal flex-1 text-left">
                Edit profile
              </p>
              <div className="flex-shrink-0">
                <img src={arrow} alt="Arrow Icon" className="w-4 h-4 sm:w-auto" />
              </div>
            </div>
          </DefaultButton>
        </div>
      </div>

      {/* QR Code Modal */}
      {isModalOpen && !isToggled && (
        <QrCodeModal
          open={isModalOpen}
          setIsOpen={handleCloseModal}
          setIsloading={setLoading}
          loading={loading}
          qrCodeUrl={qrCodeData?.qrCodeUrl}
          setIsToggled={setIsToggled}
        />
      )}
    </PageWrapper>
  )
}

export default Settings